// Every registry module deployed in the fleet: who calls it, which versions
// are out there, and how far behind the newest deployed version they sit.

import React, { useMemo, useState } from 'react';
import {
  Box,
  InputAdornment,
  Link as MuiLink,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { Search as SearchIcon, ViewModule as ModulesIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';
import { useFleetModules } from '../../hooks/useFleetModules';
import { getColors, rounded } from '../../theme/tokens';
import { ModuleSummary, modulePath } from '../../lib/fleetModules';
import { FleetLoadNote, LagBar, SegmentBar, versionHue } from '../../components/infra/charts';

type SortKey = 'name' | 'jobs' | 'apps' | 'calls' | 'versions' | 'current';

const currentShare = (m: ModuleSummary) => (m.directUsages > 0 ? m.lagCounts.current / m.directUsages : 1);

const SORTERS: Record<SortKey, (a: ModuleSummary, b: ModuleSummary) => number> = {
  name: (a, b) => a.name.localeCompare(b.name),
  jobs: (a, b) => a.jobs.length - b.jobs.length,
  apps: (a, b) => a.apps.length - b.apps.length,
  calls: (a, b) => a.usages.length - b.usages.length,
  versions: (a, b) => a.versions.length - b.versions.length,
  current: (a, b) => currentShare(a) - currentShare(b),
};

const COLUMNS: Array<{ key: SortKey; label: string; align?: 'right' }> = [
  { key: 'name', label: 'Module' },
  { key: 'jobs', label: 'Jobs', align: 'right' },
  { key: 'apps', label: 'Apps', align: 'right' },
  { key: 'calls', label: 'Calls', align: 'right' },
  { key: 'versions', label: 'Versions', align: 'right' },
];

const ModulesPage: React.FC = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const c = getColors(mode);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { fleet, progress, loading, registry } = useFleetModules();
  const [query, setQuery] = useState('');

  const sortKey = (searchParams.get('sort') as SortKey) || 'jobs';
  const sortDir = searchParams.get('dir') === 'asc' ? 'asc' : 'desc';
  const setSort = (key: SortKey) =>
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      const dir = key === sortKey && sortDir === 'desc' ? 'asc' : 'desc';
      p.set('sort', key);
      p.set('dir', dir);
      return p;
    });

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? fleet.modules.filter(m => m.source.toLowerCase().includes(q) || m.apps.some(a => a.toLowerCase().includes(q)))
      : fleet.modules;
    const sorter = SORTERS[sortKey] || SORTERS.jobs;
    return [...filtered].sort((a, b) => (sortDir === 'asc' ? 1 : -1) * sorter(a, b) || a.name.localeCompare(b.name));
  }, [fleet.modules, query, sortKey, sortDir]);

  return (
    <Box>
      <PageHeader
        title="Modules"
        crumbs={[{ label: 'Infrastructure', to: '/infra' }, { label: 'Modules' }]}
        subtitle="Registry modules deployed across the fleet. Local (../) modules are left out."
        actions={
          <TextField
            size="small"
            placeholder="Filter modules or apps"
            value={query}
            onChange={e => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />
        }
      />
      <FleetLoadNote loaded={progress.loaded} total={progress.total} loading={loading} registry={registry} />

      {!loading && fleet.modules.length === 0 ? (
        <EmptyState icon={<ModulesIcon />} title="No modules yet" body="No terraform job has recorded its modules." />
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: `${rounded.lg}px`, borderColor: c.hairline }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {COLUMNS.map(col => (
                  <TableCell key={col.key} align={col.align} sortDirection={sortKey === col.key ? sortDir : false}>
                    <TableSortLabel active={sortKey === col.key} direction={sortKey === col.key ? sortDir : 'desc'} onClick={() => setSort(col.key)}>
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell sx={{ minWidth: 160 }}>Calls by version</TableCell>
                <TableCell sx={{ minWidth: 160 }} sortDirection={sortKey === 'current' ? sortDir : false}>
                  <TableSortLabel active={sortKey === 'current'} direction={sortKey === 'current' ? sortDir : 'desc'} onClick={() => setSort('current')}>
                    Currency
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(m => (
                <TableRow key={m.source} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(modulePath(m.source))}>
                  <TableCell sx={{ maxWidth: 360 }}>
                    <MuiLink
                      component={RouterLink}
                      to={modulePath(m.source)}
                      underline="hover"
                      onClick={e => e.stopPropagation()}
                      noWrap
                      sx={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', color: c.ink }}
                    >
                      {m.name}
                    </MuiLink>
                    <Typography variant="caption" sx={{ color: c.inkFaint, display: 'block' }} noWrap title={m.source}>
                      {m.source}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{m.jobs.length}</TableCell>
                  <TableCell align="right">{m.apps.length}</TableCell>
                  <TableCell align="right" title={`${m.directUsages} direct, ${m.usages.length - m.directUsages} nested`}>
                    {m.usages.length}
                  </TableCell>
                  <TableCell align="right">
                    {m.versions.length}
                    <Typography variant="caption" sx={{ color: c.inkFaint, display: 'block' }}>
                      latest {m.newest}
                      {m.latestSource === 'deployed' && ' (deployed)'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <SegmentBar
                      segments={m.versions.map((v, i) => ({
                        key: v.version,
                        label: v.version,
                        value: v.usages,
                        color: versionHue(mode, i),
                      }))}
                    />
                  </TableCell>
                  <TableCell>
                    {m.directUsages > 0 ? (
                      <LagBar counts={m.lagCounts} height={10} legend={false} />
                    ) : (
                      <Typography variant="caption" sx={{ color: c.inkFaint }}>
                        nested only
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ModulesPage;
